import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import {
  CLOSED_OR_PARKED_STAGES,
  EMPTY_SERVICE_REQUEST_FILTERS,
  activeServiceRequestFilterCount,
  fetchImplementations,
  fetchServiceRequestBoard,
  fetchServiceRequestList,
  isClosedOrParkedStage,
  parseServiceRequestListFilters,
  serviceRequestListFiltersToSearchParams,
  stageIndex,
  stageLabel,
  type ImplementationSlim,
  type PersonRef,
  type ServiceRequestListFilters,
  type ServiceRequestListItem,
} from '../../lib/service-request-views';
import {
  fetchServiceRequestWorkspace,
  listServiceRequestTypes,
  type ServiceRequestType,
} from '../../lib/service-requests';
import { memberDisplayName, type MemberDto, type ProjectDto, type StateDto } from '../../lib/work-types';
import DraftCreatedDialog from './DraftCreatedDialog';
import { useOverlays } from './overlay-context';
import { BrandEmpty, ListEmpty, LoadingRows, WorkPageError } from './page-states';
import Peek from './Peek';
import ServiceRequestCardFace from './ServiceRequestCardFace';
import ServiceRequestDeleteMenu from './ServiceRequestDeleteMenu';
import ServiceRequestFilters from './ServiceRequestFilters';
import WorkKanban from './WorkKanban';
import { columnItemsForState, neighboursForDrop, orderedItemKeys } from './kanban-rank';

/** Columns and their drop targets: a stage the stream has no state for cannot be moved into. */
type BoardColumn = { id: string; name: string; color: string; stage: string };

async function loadServiceRequestProject(): Promise<{ project: ProjectDto; states: StateDto[]; projects: ProjectDto[] }> {
  const [workspace, projects] = await Promise.all([
    fetchServiceRequestWorkspace(),
    apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
  ]);
  return { ...workspace, projects: projects.items };
}

async function loadOwners(projectId: string): Promise<PersonRef[]> {
  const members = await apiFetch<{ items: MemberDto[] }>(v1Path(`/projects/${projectId}/members`));
  return members.items
    .filter((member) => member.is_active)
    .map((member) => ({ user_id: member.user_id, name: memberDisplayName(member), email: member.email ?? null }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The §23 board: one column per workflow stage, in PRD order. Changes Required, Rejected and
 * Cancelled stay off the board until asked for so they do not clutter the primary workflow.
 */
export default function ServiceRequestBoard({ onTotalChange }: { onTotalChange?: (total: number | null) => void }) {
  const { toast } = useOverlays();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseServiceRequestListFilters(searchParams), [searchParams]);
  const showParked = searchParams.get('show_parked') === '1';
  const createdKey = searchParams.get('created');
  const highlightKey = createdKey ?? searchParams.get('highlight');
  const onTotalChangeRef = useRef(onTotalChange);
  onTotalChangeRef.current = onTotalChange;

  const [items, setItems] = useState<ServiceRequestListItem[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const [boardEmpty, setBoardEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<ServiceRequestType[]>([]);
  const [owners, setOwners] = useState<PersonRef[]>([]);
  const [implementations, setImplementations] = useState<ImplementationSlim[]>([]);
  const [streams, setStreams] = useState<ProjectDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    void loadServiceRequestProject()
      .then(async (found) => {
        if (cancelled) return;
        setProjectId(found.project.id);
        setStates(found.states);
        setStreams(found.projects);
        const rows = await loadOwners(found.project.id);
        if (!cancelled) setOwners(rows);
      })
      .catch(() => undefined);
    void listServiceRequestTypes().then((rows) => { if (!cancelled) setTypes(rows); }).catch(() => undefined);
    void fetchImplementations().then((rows) => { if (!cancelled) setImplementations(rows); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const [board, unfiltered] = await Promise.all([
        fetchServiceRequestBoard(filters),
        activeServiceRequestFilterCount(filters) > 0
          ? fetchServiceRequestList({ ...EMPTY_SERVICE_REQUEST_FILTERS, limit: 1 })
          : null,
      ]);
      setItems(board.items);
      setCapped(board.capped);
      setBoardEmpty((unfiltered?.page.total ?? board.total) === 0);
      onTotalChangeRef.current?.(board.total);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load Service Requests'));
      setItems([]);
      setBoardEmpty(false);
      onTotalChangeRef.current?.(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load({ silent: true });
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  function applyFilters(patch: Partial<ServiceRequestListFilters>) {
    setSearchParams(serviceRequestListFiltersToSearchParams({ ...filters, ...patch }, searchParams), { replace: true });
  }

  function clearFilters() {
    const params = serviceRequestListFiltersToSearchParams(EMPTY_SERVICE_REQUEST_FILTERS, searchParams);
    params.delete('show_parked');
    setSearchParams(params, { replace: true });
  }

  function toggleParked(next: boolean) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('show_parked', '1');
    else params.delete('show_parked');
    setSearchParams(params, { replace: true });
  }

  const columns = useMemo<BoardColumn[]>(() => {
    const chosen = new Set(filters.stage);
    return states
      .filter((state) => Boolean(state.service_request_stage))
      .filter((state) => {
        const stage = state.service_request_stage as string;
        if (chosen.size > 0) return chosen.has(stage);
        return showParked || !isClosedOrParkedStage(stage);
      })
      .sort((a, b) => stageIndex(a.service_request_stage as string) - stageIndex(b.service_request_stage as string))
      .map((state) => ({
        id: state.id,
        name: state.name,
        color: state.color,
        stage: state.service_request_stage as string,
      }));
  }, [states, filters.stage, showParked]);

  /** Changes Required is back with the requestor, so it sits in Draft rather than vanishing. */
  const visible = useMemo(() => {
    const columnIds = new Set(columns.map((column) => column.id));
    const draftColumn = columns.find((column) => column.stage === 'draft');
    const hasOwnColumn = columns.some((column) => column.stage === 'changes_required');
    return items
      .map((item) => (item.stage === 'changes_required' && !hasOwnColumn && draftColumn
        ? { ...item, state_id: draftColumn.id }
        : item))
      .filter((item) => columnIds.has(item.state_id));
  }, [items, columns]);

  const peekKeys = useMemo(() => orderedItemKeys(columns, visible), [columns, visible]);

  async function move(item: ServiceRequestListItem, destStateId: string, insertBeforeId: string | null) {
    if (!projectId) return;
    const snapshot = items;
    const neighbours = neighboursForDrop(columnItemsForState(items, destStateId), item.id, insertBeforeId);
    setItems((current) => current.map((row) => (row.id === item.id ? { ...row, state_id: destStateId } : row)));
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${item.id}/move`), {
        method: 'POST',
        body: JSON.stringify({ version: item.version, state_id: destStateId, ...neighbours }),
      });
      await load({ silent: true });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      setItems(snapshot);
      const target = columns.find((column) => column.id === destStateId);
      toast(apiErrorMessage(err, `Could not move ${item.key}${target ? ` to ${stageLabel(target.stage)}` : ''}`));
    }
  }

  const parkedLabel = CLOSED_OR_PARKED_STAGES.map((stage) => stageLabel(stage)).join(', ');
  const created = createdKey ? items.find((item) => item.key === createdKey) ?? null : null;
  const createdType = created ? types.find((type) => type.id === created.request_type_id) : undefined;
  const createdStream = streams.find((stream) => stream.id === createdType?.stage_config?.approved?.linked_project_id)?.name ?? null;

  /** Fill in now opens the draft; later keeps the card outlined so it is easy to find. */
  function dismissCreated(openNow: boolean) {
    if (!createdKey) return;
    const params = new URLSearchParams(searchParams);
    params.delete('created');
    if (openNow) params.set('peek', createdKey);
    else params.set('highlight', createdKey);
    setSearchParams(params, { replace: true });
  }

  return (
    <>
      <ServiceRequestFilters
        filters={filters}
        options={{ types, owners, implementations }}
        onChange={applyFilters}
        onClear={clearFilters}
        parked={{ checked: showParked, label: parkedLabel, onChange: toggleParked }}
      />

      <DraftCreatedDialog
        requestKey={created && created.stage === 'draft' ? created.key : null}
        type={createdType}
        streamName={createdStream}
        onFillIn={() => dismissCreated(true)}
        onLater={() => dismissCreated(false)}
      />

      <WorkPageError error={error} />

      {error ? null : loading ? (
        <LoadingRows columns={6} />
      ) : boardEmpty ? (
        <BrandEmpty title="No service requests." />
      ) : columns.length === 0 ? (
        <ListEmpty
          title="No stages to show"
          description="The Service Request workflow has no states mapped to a stage."
        />
      ) : visible.length === 0 && activeServiceRequestFilterCount(filters) > 0 ? (
        <ListEmpty
          title="No matches"
          description="Nothing matches the current filters."
          action={
            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <>
          {capped ? (
            <p className="text-xs text-muted-foreground">
              Showing the first {visible.length} requests. Filter to narrow the board.
            </p>
          ) : null}
          <WorkKanban
            projectId={projectId ?? undefined}
            columns={columns}
            items={visible}
            showEmptyGroups
            canReorder={Boolean(projectId)}
            onMove={(item, destStateId, insertBeforeId) => void move(item, destStateId, insertBeforeId)}
            renderActions={(item) => <ServiceRequestDeleteMenu request={item} />}
            renderCardFace={({ item, select, actions }) => (
              <ServiceRequestCardFace request={item} select={select} actions={actions} highlight={item.key === highlightKey} />
            )}
          />
        </>
      )}

      <Peek itemKeys={peekKeys} expanded />
    </>
  );
}
